import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogIn, Eye, EyeOff, AlertCircle, ArrowLeft, UserPlus, Mail } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import RegisterBusinessForm from '@/components/features/auth/RegisterBusinessForm'
import RegisterClientForm from '@/components/features/auth/RegisterClientForm'

const C = {
  teal: '#0F5C5C',
  tealDk: '#153D3D',
  terra: '#D9785B',
  amber: '#D3A34A',
  border: '#E1E5E3',
  ink: '#202523',
  ink2: '#6D7471',
  error: '#C85C5C',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

type Tab = 'login' | 'register' | 'register-client'

export default function LoginPage() {
  const { signIn, user, profile, loading } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Cuando loading termina y hay usuario logueado → navegar según rol
  useEffect(() => {
    if (!user || loading) return
    queryClient.removeQueries()
    if (profile?.role === 'business') {
      navigate('/dashboard', { replace: true })
      return
    }
    if (profile?.role === 'admin') {
      navigate('/admin', { replace: true })
      return
    }
    navigate('/', { replace: true })
  }, [user, profile, loading]) // eslint-disable-line react-hooks/exhaustive-deps

  const [tab, setTab] = useState<Tab>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // Autocompletar con el primer email de prueba en desarrollo
  // (quitar en producción)
  const [showPass, setShowPass] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [clientNeedsConfirm, setClientNeedsConfirm] = useState(false)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await signIn(email, password)
      // useAuth (onAuthStateChange) carga el perfil y actualiza el store.
      // RequireAuth espera a que loading sea false antes de verificar el rol.
      // La navegación la maneja el useEffect de abajo.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Correo o contraseña incorrectos.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      style={{
        height: '100dvh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'safe center',
        padding: '24px 16px',
        background: `radial-gradient(120% 80% at 50% 0%, #17706f, ${C.teal} 45%, ${C.tealDk})`,
      }}
    >
      {/* Logo — vuelve al inicio */}
      <button
        onClick={() => navigate('/')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 28,
          animation: 'pxFadeUp .4s ease',
          flexShrink: 0,
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50% 50% 50% 6px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span style={{ fontSize: 22, color: C.teal }}>⚡</span>
        </div>
        <span
          style={{
            fontFamily: FH,
            fontWeight: 800,
            fontSize: 26,
            color: '#fff',
            letterSpacing: '-0.02em',
          }}
        >
          Párchala
        </span>
      </button>

      {/* Card */}
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          background: '#fff',
          borderRadius: 22,
          boxShadow: '0 24px 60px rgba(0,0,0,.22)',
          overflow: 'hidden',
          animation: 'pxFadeUp .5s ease',
          flexShrink: 0,
        }}
      >
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
          {(['login', 'register'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                flex: 1,
                padding: 16,
                border: 'none',
                borderBottom: `2.5px solid ${tab === t ? C.teal : 'transparent'}`,
                background: 'transparent',
                fontFamily: FH,
                fontWeight: 700,
                fontSize: 15,
                color: tab === t ? C.teal : C.ink2,
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {t === 'login' ? 'Ingresar' : 'Registrar negocio'}
            </button>
          ))}
        </div>

        {/* ── PANE LOGIN ── */}
        {tab === 'login' && (
          <div style={{ padding: '28px 28px 30px' }}>
            <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 21, marginBottom: 4 }}>
              Bienvenido de nuevo
            </div>
            <div style={{ fontSize: 13.5, color: C.ink2, marginBottom: 24, lineHeight: 1.4 }}>
              Ingresa con tu correo y contraseña para acceder a tu panel.
            </div>

            {error && (
              <div
                style={{
                  padding: '11px 14px',
                  borderRadius: 10,
                  fontSize: 13.5,
                  fontWeight: 600,
                  marginBottom: 16,
                  background: '#fef2f2',
                  color: C.error,
                  border: '1px solid #fecaca',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                }}
              >
                <AlertCircle size={17} color={C.error} />
                {error}
              </div>
            )}

            <form onSubmit={handleLogin}>
              {/* Email */}
              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.ink2,
                    marginBottom: 6,
                  }}
                >
                  Correo electrónico
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  required
                  autoComplete="email"
                  style={{
                    width: '100%',
                    border: `1px solid ${C.border}`,
                    borderRadius: 11,
                    padding: '12px 14px',
                    fontFamily: FB,
                    fontSize: 14,
                    color: C.ink,
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = C.teal)}
                  onBlur={(e) => (e.target.style.borderColor = C.border)}
                />
              </div>

              {/* Contraseña */}
              <div style={{ marginBottom: 20 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.ink2,
                    marginBottom: 6,
                  }}
                >
                  Contraseña
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    style={{
                      width: '100%',
                      border: `1px solid ${C.border}`,
                      borderRadius: 11,
                      padding: '12px 44px 12px 14px',
                      fontFamily: FB,
                      fontSize: 14,
                      color: C.ink,
                      outline: 'none',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = C.teal)}
                    onBlur={(e) => (e.target.style.borderColor = C.border)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 4,
                    }}
                  >
                    {showPass ? (
                      <EyeOff size={17} color={C.ink2} />
                    ) : (
                      <Eye size={17} color={C.ink2} />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: 14,
                  borderRadius: 12,
                  border: 'none',
                  background: C.teal,
                  color: '#fff',
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <LogIn size={18} />
                {submitting ? 'Ingresando…' : 'Ingresar'}
              </button>

              <button
                type="button"
                onClick={() => setTab('register-client')}
                style={{
                  width: '100%',
                  marginTop: 10,
                  padding: 13,
                  borderRadius: 12,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  color: C.ink,
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                }}
              >
                <UserPlus size={17} />
                Registrarme
              </button>

              <button
                type="button"
                onClick={() => navigate('/')}
                style={{
                  width: '100%',
                  marginTop: 10,
                  padding: 12,
                  borderRadius: 12,
                  border: 'none',
                  background: 'transparent',
                  color: C.ink2,
                  fontFamily: FB,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <ArrowLeft size={16} />
                Volver
              </button>
            </form>

            <div
              style={{
                margin: '18px 0 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                color: C.ink2,
                fontSize: 12.5,
              }}
            >
              <span style={{ flex: 1, height: 1, background: C.border }} />
              o
              <span style={{ flex: 1, height: 1, background: C.border }} />
            </div>

            <button
              onClick={() => setTab('register')}
              style={{
                width: '100%',
                padding: 13,
                borderRadius: 12,
                border: `1px solid ${C.border}`,
                background: '#fff',
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                color: C.ink,
              }}
            >
              Registrar mi negocio
            </button>
          </div>
        )}

        {/* ── PANE REGISTRO ── */}
        {tab === 'register' && !success && (
          <div style={{ padding: '28px 28px 30px' }}>
            <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 21, marginBottom: 4 }}>
              Registrar negocio
            </div>
            <div style={{ fontSize: 13.5, color: C.ink2, marginBottom: 24, lineHeight: 1.4 }}>
              Completa el formulario. El equipo de Párchala revisará tu solicitud.
            </div>
            <RegisterBusinessForm onSuccess={() => setSuccess(true)} />
          </div>
        )}

        {/* ── SUCCESS ── */}
        {tab === 'register' && success && (
          <div style={{ padding: '40px 28px', textAlign: 'center' }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 20,
                background: '#3E8B63',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: 36,
              }}
            >
              ✓
            </div>
            <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 22, marginBottom: 10 }}>
              Solicitud enviada
            </div>
            <div style={{ color: C.ink2, fontSize: 14, lineHeight: 1.55, marginBottom: 24 }}>
              Recibimos tu solicitud. El equipo de Párchala la revisará y te contactará pronto.
            </div>
            <button
              onClick={() => navigate('/')}
              style={{
                maxWidth: 260,
                width: '100%',
                padding: 13,
                borderRadius: 12,
                border: 'none',
                background: C.teal,
                color: '#fff',
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Volver al inicio
            </button>
          </div>
        )}

        {/* ── PANE REGISTRO CLIENTE ── */}
        {tab === 'register-client' && !clientNeedsConfirm && (
          <div style={{ padding: '28px 28px 30px' }}>
            <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 21, marginBottom: 4 }}>
              Crear cuenta
            </div>
            <div style={{ fontSize: 13.5, color: C.ink2, marginBottom: 24, lineHeight: 1.4 }}>
              Regístrate para guardar planes, marcar interés y comentar.
            </div>
            <RegisterClientForm
              onSuccess={(needsEmailConfirmation) => {
                if (needsEmailConfirmation) setClientNeedsConfirm(true)
                // Si no, ya llegó la sesión — el useEffect de arriba redirige solo.
              }}
            />
            <button
              type="button"
              onClick={() => setTab('login')}
              style={{
                width: '100%',
                marginTop: 14,
                padding: 12,
                borderRadius: 12,
                border: 'none',
                background: 'transparent',
                color: C.ink2,
                fontFamily: FB,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <ArrowLeft size={16} />
              Ya tengo cuenta
            </button>
          </div>
        )}

        {/* ── SUCCESS CLIENTE (requiere confirmar correo) ── */}
        {tab === 'register-client' && clientNeedsConfirm && (
          <div style={{ padding: '40px 28px', textAlign: 'center' }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 20,
                background: C.teal,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <Mail size={32} color="#fff" />
            </div>
            <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 22, marginBottom: 10 }}>
              Confirma tu correo
            </div>
            <div style={{ color: C.ink2, fontSize: 14, lineHeight: 1.55, marginBottom: 24 }}>
              Te enviamos un enlace de confirmación. Ábrelo para activar tu cuenta y luego inicia
              sesión.
            </div>
            <button
              onClick={() => {
                setClientNeedsConfirm(false)
                setTab('login')
              }}
              style={{
                maxWidth: 260,
                width: '100%',
                padding: 13,
                borderRadius: 12,
                border: 'none',
                background: C.teal,
                color: '#fff',
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Ir a iniciar sesión
            </button>
          </div>
        )}
      </div>

      {/* Hint credenciales */}
      <div
        style={{
          marginTop: 20,
          padding: '12px 16px',
          background: 'rgba(255,255,255,.08)',
          borderRadius: 12,
          color: 'rgba(255,255,255,.75)',
          fontSize: 12,
          lineHeight: 1.8,
          animation: 'pxFadeUp .6s ease',
          maxWidth: 440,
          width: '100%',
          flexShrink: 0,
        }}
      >
        <strong style={{ color: '#fff' }}>Credenciales de prueba</strong>
        <br />
        cliente@parchala.co · Parchala2026!
        <br />
        negocio@parchala.co · Parchala2026!
        <br />
        admin@parchala.co &nbsp; · Parchala2026!
      </div>
    </div>
  )
}
